// Portal API client - simplified for MSP customer access
// Same-origin requests via CloudFront /api/* behavior (prod) or vite dev
// proxy (local). No VITE_API_URL needed — keeps cookies first-party and
// avoids CORS preflight on every mutation.
import { getPortalCSRFToken } from "./portal-csrf";

const API_BASE = "/api/v1";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * In-flight refresh promise. De-dupes concurrent 401s so a burst of
 * parallel requests after a 15-minute idle fires /portal/refresh ONCE,
 * not N times. Concurrent refreshes would trip the backend's single-use
 * rotation and log the customer out as suspected token theft.
 */
let inflightRefresh: Promise<boolean> | null = null;

async function attemptPortalRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE}/portal/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return res.ok;
    } catch {
      return false;
    }
  })();
  // Allow other 401s in this microtask burst to await the same promise,
  // then clear so a future expiry can trigger a fresh attempt.
  inflightRefresh.finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

// Base fetch wrapper with authentication and silent refresh-on-401
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  // Build and send the request, re-reading the CSRF token each time so the
  // rotated cookie from /portal/refresh is picked up on retries.
  const doFetch = () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // Add CSRF token for mutations
    const csrfToken = getPortalCSRFToken();
    if (csrfToken && options.method && options.method !== "GET") {
      headers["X-CSRF-Token"] = csrfToken;
    }

    return fetch(url, {
      ...options,
      headers,
      credentials: "include", // Include httpOnly cookies
    });
  };

  let response = await doFetch();

  // Silently refresh on 401 and retry exactly once. Never recurse on the
  // refresh endpoint itself to prevent infinite loops.
  if (response.status === 401 && !endpoint.startsWith("/portal/refresh")) {
    const ok = await attemptPortalRefresh();
    if (ok) {
      response = await doFetch();
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      // Refresh failed or this is the refresh endpoint — bounce to login.
      window.location.href = "/login";
      throw new ApiError(401, "Authentication required");
    }

    const error = await response.json().catch(() => ({ detail: "Request failed" }));
    throw new ApiError(response.status, error.detail || "Request failed");
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) return {} as T;

  return JSON.parse(text);
}

/**
 * Build a query string, dropping null/undefined/empty values.
 *
 * `domain_id: null` means "all domains in the org" and must be omitted rather
 * than sent as the string "null", which the API would reject as a bad int.
 */
function qs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === false) continue;
    query.set(key, String(value));
  }
  const str = query.toString();
  return str ? `?${str}` : "";
}

// Portal API methods
export const portalApi = {
  // Dashboard overview. Omitting domainId aggregates the whole customer org.
  getDashboard: (params?: { domain_id?: number | null }) =>
    fetchApi<PortalDashboard>(`/portal/dashboard${qs(params)}`),

  // Domains (view only)
  listDomains: (params?: { page?: number; size?: number }) =>
    fetchApi<PortalDomainsResponse>(`/portal/domains${qs(params)}`),

  // Reports (view only)
  listReports: (params?: {
    domain_id?: number | null;
    page?: number;
    size?: number;
  }) => fetchApi<PortalReportsResponse>(`/portal/reports${qs(params)}`),

  // Trends (view only)
  getTrends: (params?: { domain_id?: number | null; period_days?: number }) =>
    fetchApi<PortalTrends>(`/portal/trends${qs(params)}`),

  // Senders (view only)
  listSenders: (params?: {
    domain_id?: number | null;
    page?: number;
    size?: number;
  }) => fetchApi<PortalSendersResponse>(`/portal/senders${qs(params)}`),

  // Alerts (view only)
  listAlerts: (params?: {
    domain_id?: number | null;
    page?: number;
    size?: number;
    unread_only?: boolean;
  }) => fetchApi<PortalAlertsResponse>(`/portal/alerts${qs(params)}`),

  // Drill-downs. Anything outside the customer org 404s server-side.
  getReport: (reportId: number, params?: { page?: number; size?: number }) =>
    fetchApi<PortalReportDetail>(`/portal/reports/${reportId}${qs(params)}`),

  getSender: (sourceIp: string) =>
    fetchApi<PortalSenderDetail>(`/portal/senders/${encodeURIComponent(sourceIp)}`),

  getDomain: (domainId: number) =>
    fetchApi<PortalDomain>(`/portal/domains/${domainId}`),

  // Insights — why mail is failing, and what to fix next.
  getFailureSummary: (params?: { domain_id?: number | null; days?: number }) =>
    fetchApi<PortalFailureSummary>(`/portal/failures/summary${qs(params)}`),

  getFailureSources: (params?: {
    domain_id?: number | null;
    reason?: FailureReason | null;
    days?: number;
    size?: number;
  }) => fetchApi<PortalFailureSourcesResponse>(`/portal/failures/sources${qs(params)}`),

  getActionCenter: (params?: { domain_id?: number | null; days?: number }) =>
    fetchApi<PortalActionCenterResponse>(`/portal/action-center${qs(params)}`),

  // Protection surface — all read-only. There is deliberately no lookalike
  // scan trigger: scans spend a shared certspotter free-tier budget and stay
  // MSP-initiated.
  getDnsHistory: (params?: {
    domain_id?: number | null;
    days?: number;
    changes_only?: boolean;
    size?: number;
  }) => fetchApi<PortalDnsHistoryResponse>(`/portal/dns-history${qs(params)}`),

  getTlsReports: (params?: { domain_id?: number | null; days?: number }) =>
    fetchApi<PortalTlsReportsResponse>(`/portal/tls-reports${qs(params)}`),

  getLookalikes: (params?: { domain_id?: number | null }) =>
    fetchApi<PortalLookalikeResponse>(`/portal/lookalikes${qs(params)}`),

  // User profile
  getProfile: () => fetchApi<PortalUser>("/portal/profile"),

  // Settings — read one domain's notification preferences. Settings are stored
  // per domain, so an org-wide session must say which one; omitting domainId
  // falls back server-side to the domain used to log in.
  getSettings: (params?: { domain_id?: number | null }) =>
    fetchApi<PortalSettings>(`/portal/settings${qs(params)}`),

  // Settings — persist a partial update of the notification preferences.
  // fetchApi adds the CSRF header and retries on a silent token refresh.
  // 422 (invalid alert_threshold) surfaces via ApiError.detail as a toast.
  updateSettings: (
    data: Partial<PortalSettings>,
    params?: { domain_id?: number | null }
  ) =>
    fetchApi<PortalSettings>(`/portal/settings${qs(params)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // DNS check — re-validates SPF/DKIM/DMARC records for one of the caller's
  // domains. An id outside the customer org is a 404, enforced server-side.
  checkDns: (params?: { domain_id?: number | null }) =>
    fetchApi<PortalDnsCheck>(`/portal/dns-check${qs(params)}`),

  // DMARC policy rollout (advise + verify) for ONE domain in the caller's org.
  // getRollout may resolve null (JSON.parse("null")) when no rollout is active.
  getRollout: (params?: { domain_id?: number | null }) =>
    fetchApi<PortalRollout | null>(`/portal/rollout${qs(params)}`),

  // Start a rollout. 409 if one is already active (surfaced via ApiError.detail).
  createRollout: (
    body: { target_policy?: "quarantine" | "reject" } = {},
    params?: { domain_id?: number | null }
  ) =>
    fetchApi<PortalRollout>(`/portal/rollout${qs(params)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Advance to the next stage. 409 + human-readable reason if a safety gate
  // blocks the advance; that reason is surfaced to the customer as a toast.
  advanceRollout: (params?: { domain_id?: number | null }) =>
    fetchApi<PortalRollout>(`/portal/rollout/advance${qs(params)}`, {
      method: "POST",
    }),
};

// Portal-specific types
export interface PortalUser {
  id: string;
  email: string;
  name: string | null;
  customer_id: number;
  customer_name: string;
  msp_name: string;
}

export interface PortalDashboard {
  domains_count: number;
  reports_count: number;
  pass_rate: number;
  alerts_count: number;
  recent_activity: PortalActivity[];
}

export interface PortalActivity {
  id: number;
  type: string;
  message: string;
  created_at: string;
}

export interface PortalDomain {
  id: number;
  domain: string;
  is_verified: boolean;
  pass_rate: number;
  reports_count: number;
  last_report_at: string | null;
}

export interface PortalDomainsResponse {
  items: PortalDomain[];
  total: number;
  page: number;
  size: number;
}

export interface PortalReport {
  id: number;
  domain: string;
  org_name: string;
  date_range_begin: string;
  date_range_end: string;
  total_emails: number;
  passed_emails: number;
  failed_emails: number;
  pass_rate: number;
}

export interface PortalReportsResponse {
  items: PortalReport[];
  total: number;
  page: number;
  size: number;
}

export interface PortalTrends {
  data: Array<{
    date: string;
    total_emails: number;
    passed_emails: number;
    pass_rate: number;
  }>;
  period_start: string;
  period_end: string;
}

export interface PortalSender {
  source_ip: string;
  total_emails: number;
  passed_emails: number;
  failed_emails: number;
  pass_rate: number;
  country: string | null;
  asn_org: string | null;
}

export interface PortalSendersResponse {
  items: PortalSender[];
  total: number;
  page: number;
  size: number;
}

export interface PortalAlert {
  id: number;
  type: string;
  severity: string; // backend Notification.severity: "info" | "warning" | "critical" (String(20))
  title: string;
  message: string;
  domain: string | null;
  is_read: boolean;
  created_at: string;
}

export interface PortalAlertsResponse {
  items: PortalAlert[];
  total: number;
  unread_count: number;
  page: number;
  size: number;
}

export interface PortalSettings {
  email_notifications: boolean;
  weekly_digest: boolean;
  alert_threshold: "all" | "high" | "critical";
}

export interface PortalDnsRecord {
  exists: boolean;
  valid: boolean;
  record: string | null;
  errors: string[];
  warnings: string[];
  dkim_alignment_note?: string | null;
}

export interface PortalDnsCheck {
  domain: string;
  spf: PortalDnsRecord;
  dkim: PortalDnsRecord;
  dmarc: PortalDnsRecord;
  all_valid: boolean;
  overall_status: "pass" | "warning" | "fail";
}

export interface PortalRolloutStage {
  policy: string;
  wait_days: number;
  pct: number;
  generated_record: string;
  is_current: boolean;
  is_completed: boolean;
}

export interface PortalRollout {
  status: "pending" | "active" | "paused" | "blocked" | "completed" | "cancelled";
  current_stage: string;
  target_stage: string;
  current_stage_index: number;
  stages: PortalRolloutStage[];
  progress_percent: number;
  last_pass_rate: number | null;
  blocked_reason: string | null;
  can_advance: boolean;
  // Advance-gate preview: false → the server would 409 an advance right now
  // (reason says why, verbatim); null → the rollout can't structurally advance
  // (completed / paused / final stage) so a gate check is meaningless.
  advance_gate_passed: boolean | null;
  advance_gate_reason: string | null;
  dns_record_name: string;
  current_dns_record: string;
  started_at: string | null;
  next_transition_at: string | null;
}

export interface PortalReportRecord {
  id: number;
  source_ip: string;
  count: number;
  disposition: string | null;
  dkim_result: string | null;
  spf_result: string | null;
  header_from: string | null;
  envelope_from: string | null;
  dkim_domain: string | null;
  country: string | null;
  asn_org: string | null;
  ptr: string | null;
}

export interface PortalReportDetail {
  id: number;
  domain: string;
  domain_id: number;
  org_name: string;
  report_id: string;
  date_range_begin: string;
  date_range_end: string;
  policy_p: string | null;
  policy_sp: string | null;
  policy_pct: number | null;
  policy_adkim: string | null;
  policy_aspf: string | null;
  total_emails: number;
  passed_emails: number;
  failed_emails: number;
  pass_rate: number;
  records: PortalReportRecord[];
  records_total: number;
  page: number;
  size: number;
}

export interface PortalSenderDomainBreakdown {
  domain_id: number;
  domain: string;
  total_emails: number;
  passed_emails: number;
  failed_emails: number;
  pass_rate: number;
}

export interface PortalSenderDetail {
  source_ip: string;
  total_emails: number;
  passed_emails: number;
  failed_emails: number;
  pass_rate: number;
  dkim_pass_emails: number;
  spf_pass_emails: number;
  country: string | null;
  asn_org: string | null;
  ptr: string | null;
  first_seen: string | null;
  last_seen: string | null;
  domains: PortalSenderDomainBreakdown[];
}

export type FailureReason = "spf_only" | "dkim_only" | "both";

export interface PortalFailureReasonRow {
  reason: FailureReason;
  display_name: string;
  description: string;
  count: number;
  /** Share of FAILING mail, not of all mail. */
  percentage: number;
}

export interface PortalFailureSummary {
  total_emails: number;
  failed_emails: number;
  failure_rate: number;
  by_reason: PortalFailureReasonRow[];
  period_days: number;
}

export interface PortalFailureSource {
  source_ip: string;
  asn_org: string | null;
  country: string | null;
  total_emails: number;
  failed_emails: number;
  failure_rate: number;
}

export interface PortalFailureSourcesResponse {
  items: PortalFailureSource[];
  reason: string | null;
  period_days: number;
}

export interface PortalActionCenterItem {
  domain_id: number;
  domain: string;
  score: number;
  grade: string;
  top_issue: string | null;
  pass_rate: number;
  total_emails: number;
}

export interface PortalActionCenterResponse {
  items: PortalActionCenterItem[];
  total_domains: number;
  needs_attention: number;
  period_days: number;
}

export interface PortalDnsHistoryEntry {
  id: number;
  domain_id: number;
  domain: string;
  checked_at: string;
  overall_status: string;
  has_changes: boolean;
  changes_summary: string | null;
  spf_valid: boolean | null;
  dkim_valid: boolean | null;
  dmarc_valid: boolean | null;
}

export interface PortalDnsHistoryResponse {
  items: PortalDnsHistoryEntry[];
  total: number;
  period_days: number;
}

export interface PortalTlsReport {
  id: number;
  domain_id: number;
  domain: string;
  organization_name: string | null;
  report_date_start: string;
  report_date_end: string;
  total_successful: number;
  total_failed: number;
}

export interface PortalTlsReportsResponse {
  items: PortalTlsReport[];
  total: number;
  total_successful: number;
  total_failed: number;
  success_rate: number;
  period_days: number;
}

export interface PortalLookalike {
  id: number;
  domain_id: number;
  protected_domain: string;
  candidate_domain: string;
  risk: string;
  registered: boolean;
  has_a: boolean;
  has_mx: boolean;
  has_cert: boolean;
  sends_mail: boolean;
  serves_http: boolean;
  is_parked: boolean;
  registrar: string | null;
  resolved_ip: string | null;
  first_detected_at: string;
  last_seen_at: string;
}

export interface PortalLookalikeResponse {
  items: PortalLookalike[];
  total: number;
  high_risk: number;
}
